import React, { useState, useEffect } from "react";
import styles from "./GuidedPromptFlow.module.css";
import { useMindspaceStore } from "@store";
import { fetchContextKeyDefinitions, ContextKeyDefinition } from "../../services/apiClientV2";
import { logError } from "../../utils/logging";

interface ContextReviewStepProps {
  implicitKeys: string[];
  onContinue: (updatedValues: Record<string, string>) => void;
  onCancel: () => void;
}

const ContextReviewStep: React.FC<ContextReviewStepProps> = ({
  implicitKeys,
  onContinue,
  onCancel,
}) => {
  const mindspaceContext = useMindspaceStore((state) => state.contextItems);
  const [keyDefinitions, setKeyDefinitions] = useState<ContextKeyDefinition[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Build initial values map from mindspace context
  const initialValues: Record<string, string> = {};
  implicitKeys.forEach((key) => {
    const existing = mindspaceContext.find((c) => c.item_type === key);
    initialValues[key] = existing?.content || "";
  });

  const [values, setValues] = useState<Record<string, string>>(initialValues);

  useEffect(() => {
    let mounted = true;
    const loadKeys = async () => {
      setIsLoading(true);
      try {
        const defs = await fetchContextKeyDefinitions();
        if (mounted) {
          setKeyDefinitions(defs);
        }
      } catch (err) {
        logError("Failed to load context key definitions", err);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };
    loadKeys();
    return () => { mounted = false; };
  }, []);

  const handleChange = (key: string, v: string) => {
    setValues((prev) => ({ ...prev, [key]: v }));
  };

  const handleContinue = () => {
    onContinue(values);
  };

  const getLabel = (key: string) => {
    const def = keyDefinitions.find((d) => d.key_name === key);
    return def?.display_label || key;
  };

  const getDescription = (key: string) => {
    const def = keyDefinitions.find((d) => d.key_name === key);
    return def?.description;
  };

  // Group keys by category
  const groupedKeys = React.useMemo(() => {
    const groups: Record<string, string[]> = {};
    implicitKeys.forEach(key => {
      const def = keyDefinitions.find((d) => d.key_name === key);
      const cat = def?.category || "altele";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(key);
    });
    return groups;
  }, [implicitKeys, keyDefinitions]);

  const CATEGORY_LABELS: Record<string, string> = {
    identity: "Identitate Brand",
    strategic: "Obiective Strategice",
    context: "Context Piață",
    preferences: "Preferințe",
    altele: "Altele"
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.question}>Verifică informațiile de bază</h2>
      <p className={`${styles.progress} ${styles.progressSpaced}`}>
        Aceste informații vor fi folosite în răspunsul AI. Modifică-le dacă este
        nevoie.
      </p>

      {isLoading && <div className={styles.loadingSpinner}></div>}

      {!isLoading && Object.entries(groupedKeys).map(([category, keys]) => (
        <div key={category} className={styles.categoryGroup}>
          <h3 className={styles.categoryTitle}>{CATEGORY_LABELS[category] || category}</h3>
          {keys.map((key) => (
            <div key={key} className={styles.fieldRow}>
              <label htmlFor={`ctx-${key}`} className={styles.fieldLabel}>
                {getLabel(key)}
                {getDescription(key) && (
                  <span className={styles.fieldInfo} title={getDescription(key)}>
                    ℹ️
                  </span>
                )}
              </label>
              <textarea
                id={`ctx-${key}`}
                className={styles.input}
                value={values[key]}
                onChange={(e) => handleChange(key, e.target.value)}
                placeholder={`Introdu ${getLabel(key).toLowerCase()}...`}
              />
            </div>
          ))}
        </div>
      ))}

      <div className={styles.buttonRow}>
        <button
          className={`${styles.button} ${styles.secondary}`}
          onClick={onCancel}
        >
          Renunță
        </button>
        <button
          className={`${styles.button} ${styles.primary}`}
          onClick={handleContinue}
        >
          Continuă
        </button>
      </div>
    </div>
  );
};

export default ContextReviewStep;
